/**
 * TokenInput — one side of the swap card (either "You pay" or "You
 * receive"). Renders a token pill, the amount field, and (on the
 * "from" side) a MAX button wired to the connected wallet balance.
 *
 * Two modes:
 *   side="from"  editable, fires `onChange`, shows MAX + balance
 *   side="to"    read-only, shows the projected output from a quote
 */

import { useId, type ChangeEvent } from "react";
import type { TokenInfo } from "@/lib/tokens";
import { formatAmount } from "@/lib/format";
import { useAccount, useBalance } from "wagmi";

interface TokenInputProps {
  label: string;
  side: "from" | "to";
  token: TokenInfo;
  /** Editable decimal string (side="from") or formatted quote output. */
  value: string;
  onChange?: (next: string) => void;
  /** Only used on side="from"; disables MAX when false. */
  disabled?: boolean;
  /** Loading flag for side="to" so the output shows a shimmer. */
  loading?: boolean;
}

export function TokenInput({
  label,
  side,
  token,
  value,
  onChange,
  disabled,
  loading,
}: TokenInputProps) {
  const inputId = useId();
  const { address, isConnected } = useAccount();
  const { data: balance } = useBalance({
    address,
    token: token.address,
    query: { enabled: side === "from" && isConnected },
  });

  const balanceDisplay =
    balance !== undefined
      ? formatAmount(balance.value, token.decimals, 4)
      : "—";

  const onInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (!onChange) return;
    const v = e.target.value;
    // Permit only one dot and digits; keep leading "." allowed so the
    // user can type "0.5" naturally.
    if (v === "" || /^\d*\.?\d*$/.test(v)) onChange(v);
  };

  const onMax = () => {
    if (!onChange || !balance) return;
    onChange(formatAmount(balance.value, token.decimals, token.decimals));
  };

  return (
    <div
      className={`rounded-[--radius] border border-[--border] bg-[--bg-inset] px-4 py-3.5 transition focus-within:border-[--border-strong] focus-within:ring-2 focus-within:ring-[--ring] ${
        side === "to" ? "opacity-95" : ""
      }`}
    >
      <div className="mb-2 flex items-center justify-between text-[11px] uppercase tracking-[0.1em] text-[--text-subtle]">
        {side === "from" ? (
          <label htmlFor={inputId}>{label}</label>
        ) : (
          <span>{label}</span>
        )}
        {side === "from" && isConnected && (
          <span className="num normal-case tracking-normal text-[11px]">
            Balance: {balanceDisplay} {token.symbol}
          </span>
        )}
      </div>

      <div className="flex items-center gap-3">
        {/* Token pill — fixed since the app is ASIC-specific, a single
            pair. No dropdown needed. */}
        <div
          className="inline-flex items-center gap-2 rounded-full border border-[--border] bg-[--bg-elev] px-3 py-1.5 text-sm font-semibold text-[--text]"
          aria-label={token.name}
        >
          <span
            aria-hidden="true"
            className="grid size-6 place-items-center rounded-full text-[12px] font-bold text-white"
            style={{ background: token.color }}
          >
            {token.glyph}
          </span>
          {token.symbol}
        </div>

        {/* Amount field — big, monospaced. The `to` side is read-only
            and receives a formatted quote output. */}
        <div className="relative flex-1 text-right">
          {side === "from" ? (
            <input
              id={inputId}
              name="amount"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0.0…"
              value={value}
              onChange={onInput}
              disabled={disabled}
              className="num w-full bg-transparent text-right text-[28px] font-medium tracking-tight text-[--text] placeholder:text-[--text-subtle] focus:outline-none"
              aria-label={`Amount of ${token.symbol} to swap`}
            />
          ) : (
            <div
              role="status"
              aria-live="polite"
              className={`num text-right text-[28px] font-medium tracking-tight ${
                value && value !== "0" ? "text-[--text]" : "text-[--text-subtle]"
              } ${loading ? "opacity-60" : ""}`}
            >
              {value && value !== "0" ? value : "0"}
            </div>
          )}
        </div>

        {side === "from" && (
          <button
            type="button"
            onClick={onMax}
            disabled={!isConnected || !balance || disabled}
            className="rounded-md border border-[--border] bg-[--bg-elev] px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[--text-muted] hover:text-[--text] hover:border-[--border-strong] disabled:cursor-not-allowed disabled:opacity-40 transition"
          >
            MAX
          </button>
        )}
      </div>
    </div>
  );
}
